import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

const MM_TO_PX = 96 / 25.4;

const SizedLabelBox = ({
  widthMm,
  heightMm,
  children,
  showBorder = true,
  maxPreviewPx = 320,
  shape = 'rect',
  rotation = 0,
}) => {
  const isRotated = rotation === 90 || rotation === 270;
  const innerRef = useRef(null);
  const [naturalContent, setNaturalContent] = useState({ w: 0, h: 0 });

  const naturalW = widthMm * MM_TO_PX;
  const naturalH = heightMm * MM_TO_PX;
  const previewScale = Math.min(1, maxPreviewPx / naturalW, maxPreviewPx / naturalH);
  const previewW = naturalW * previewScale;
  const previewH = naturalH * previewScale;

  useEffect(() => {
    const inner = innerRef.current;
    if (!inner) return undefined;

    const measure = () => {
      const w = inner.scrollWidth;
      const h = inner.scrollHeight;
      if (!w || !h) return;
      setNaturalContent((prev) => {
        if (Math.abs(w - prev.w) > 0.5 || Math.abs(h - prev.h) > 0.5) {
          return { w, h };
        }
        return prev;
      });
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(inner);
    return () => ro.disconnect();
  }, []);

  const fitScale = (boxW, boxH) => {
    if (!naturalContent.w || !naturalContent.h) return 1;
    const effW = isRotated ? boxH : boxW;
    const effH = isRotated ? boxW : boxH;
    return Math.min(effW / naturalContent.w, effH / naturalContent.h);
  };

  const screenScale = fitScale(previewW, previewH);
  const printScale = fitScale(naturalW, naturalH);
  const rotateTransform = isRotated ? ` rotate(${rotation}deg)` : '';

  return (
    <Box
      sx={{
        flex: '0 0 auto',
        width: `${previewW}px`,
        height: `${previewH}px`,
        position: 'relative',
        overflow: 'hidden',
        border: showBorder ? '1px dashed' : 'none',
        borderColor: 'divider',
        borderRadius: shape === 'circle' ? '50%' : 0,
        boxSizing: 'content-box',
        '@media print': {
          border: 'none',
          borderRadius: 0,
          width: `${widthMm}mm`,
          height: `${heightMm}mm`,
        },
      }}
    >
      <Box
        ref={innerRef}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -50%)${rotateTransform} scale(${screenScale})`,
          transformOrigin: 'center center',
          '@media print': {
            transform: `translate(-50%, -50%)${rotateTransform} scale(${printScale})`,
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default SizedLabelBox;
